import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { HeatmapLayer } from '@deck.gl/aggregation-layers';
import { ScatterplotLayer } from '@deck.gl/layers';

/** Faint at low density → deep crimson at clusters (first stop is visible for single points). */
export const HEATMAP_COLOR_RANGE = [
    [255, 255, 178, 55],
    [255, 237, 160, 95],
    [254, 196, 79, 140],
    [253, 141, 60, 185],
    [252, 78, 42, 220],
    [227, 26, 28, 240],
    [177, 0, 38, 255],
    [128, 0, 38, 255],
];

export const HEATMAP_LEGEND_GRADIENT =
    'linear-gradient(90deg, #ffffb2 0%, #feb24c 35%, #fc4e2a 65%, #b10026 100%)';

export const buildComplaintHeatmapPoints = (complaints = []) =>
    complaints
        .filter((complaint) => {
            const lat = parseFloat(
                complaint?.Latitude ?? complaint?.latitude ?? complaint?.Lat ?? complaint?.lat
            );
            const lng = parseFloat(
                complaint?.Longitude ??
                    complaint?.longitude ??
                    complaint?.Long ??
                    complaint?.lng ??
                    complaint?.Lng
            );
            const status = complaint?.Status ?? complaint?.status ?? '';
            return (
                Number.isFinite(lat) &&
                Number.isFinite(lng) &&
                !(lat === 0 && lng === 0) &&
                (status === 'Pending' || status === 'Allocated')
            );
        })
        .map((complaint) => {
            const lat = parseFloat(
                complaint.Latitude ?? complaint.latitude ?? complaint.Lat ?? complaint.lat
            );
            const lng = parseFloat(
                complaint.Longitude ??
                    complaint.longitude ??
                    complaint.Long ??
                    complaint.lng ??
                    complaint.Lng
            );
            const status = complaint.Status ?? complaint.status ?? '';
            return {
                position: [lng, lat],
                weight: status === 'Pending' ? 2 : 1.2,
            };
        })
        .filter(
            (point) =>
                Number.isFinite(point.position[0]) && Number.isFinite(point.position[1])
        );

/** Compact blobs — still visible for single points, not oversized. */
export const getHeatmapRadiusForZoom = (zoom) => {
    const z = Number(zoom);
    if (!Number.isFinite(z)) return 36;
    if (z >= 13) return 18;
    if (z >= 11) return 24;
    if (z >= 9) return 30;
    if (z >= 7) return 38;
    return 46;
};

/** Moderate intensity so sparse points show faintly without huge spread. */
export const getHeatmapIntensityForCount = (count) => {
    const n = Number(count) || 0;
    if (n <= 5) return 2.4;
    if (n <= 25) return 2.0;
    if (n <= 80) return 1.7;
    if (n <= 200) return 1.4;
    return 1.2;
};

const createGlowLayer = (data, opacity = 1) =>
    new ScatterplotLayer({
        id: 'complaint-heatmap-glow',
        data,
        getPosition: (point) => point.position,
        getFillColor: [255, 210, 90, Math.round(45 * opacity)],
        getRadius: 700,
        radiusMinPixels: 5,
        radiusMaxPixels: 14,
        opacity: 0.75,
        pickable: false,
        stroked: false,
    });

const createHeatmapLayer = (data, opacity = 1, radiusPixels = 36, intensity = 1.7) =>
    new HeatmapLayer({
        id: 'complaint-heatmap-layer',
        data,
        getPosition: (point) => point.position,
        getWeight: (point) => point.weight,
        radiusPixels,
        opacity,
        colorRange: HEATMAP_COLOR_RANGE,
        threshold: 0.005,
        intensity,
    });

const buildLayerProps = (data, opacity, zoom, radiusPixels) => {
    const radius = radiusPixels ?? getHeatmapRadiusForZoom(zoom);
    const intensity = getHeatmapIntensityForCount(data.length);
    return { radius, intensity };
};

export const createComplaintHeatmapOverlay = (
    map,
    complaints = [],
    opacity = 1,
    radiusPixels
) => {
    const data = buildComplaintHeatmapPoints(complaints);
    if (!map || data.length === 0) return null;

    const zoom = typeof map.getZoom === 'function' ? map.getZoom() : 8;
    const { radius, intensity } = buildLayerProps(data, opacity, zoom, radiusPixels);

    const overlay = new GoogleMapsOverlay({
        layers: [
            createGlowLayer(data, opacity),
            createHeatmapLayer(data, opacity, radius, intensity),
        ],
    });
    overlay.setMap(map);
    overlay.__heatmapData = data;
    overlay.__heatmapOpacity = opacity;
    overlay.__heatmapRadius = radius;
    overlay.__heatmapIntensity = intensity;
    return overlay;
};

const refreshHeatmapLayer = (overlay) => {
    if (!overlay?.__heatmapData?.length) return;

    overlay.setProps({
        layers: [
            createGlowLayer(overlay.__heatmapData, overlay.__heatmapOpacity ?? 1),
            createHeatmapLayer(
                overlay.__heatmapData,
                overlay.__heatmapOpacity ?? 1,
                overlay.__heatmapRadius ?? 36,
                overlay.__heatmapIntensity ?? 1.7
            ),
        ],
    });
};

export const updateComplaintHeatmapOpacity = (overlay, opacity) => {
    if (!overlay?.__heatmapData?.length) return;

    overlay.__heatmapOpacity = opacity;
    refreshHeatmapLayer(overlay);
};

export const updateComplaintHeatmapRadius = (overlay, radiusPixels) => {
    if (!overlay?.__heatmapData?.length) return;

    overlay.__heatmapRadius = radiusPixels;
    refreshHeatmapLayer(overlay);
};

export const clearComplaintHeatmapOverlay = (overlay) => {
    if (!overlay) return;

    try {
        overlay.setMap(null);
    } catch (error) {
        // Ignore cleanup errors during map teardown.
    }

    try {
        overlay.finalize?.();
    } catch (error) {
        // Ignore cleanup errors during map teardown.
    }
};
